// The sim scene on the spike's runtime, and the handle methods main.ts puts on `window.__rpg`. The scenes share the
// one kernel game and host (host.ts): the A2 test valley (the default; src/valley/, gates A2.2 and A2.3), phase B's
// dojo (`?scene=dojo`, src/combat/, gates B1 and B2) on the valley's machinery, the B4 arena (`?scene=arena`,
// src/enemies/, gate B4), the B5 ragdoll lab (`?scene=ragdoll`, src/bodies/), the C5 things lab (`?scene=things`,
// src/things/: gathering, breakables, interaction, nav tile slicing), the D1 market lab (`?scene=market`, src/rpg/:
// progression, items and the economy, the shops, the forge and the pot) and the A1 lab (`?scene=lab`, gate A1.3's
// deterministic pen; the camera, figure and targets labs step it offstage). A `?scenario=` fixture picks its own
// scene when `?scene` is absent. Switches: `?paused` starts the clock paused (a test's deterministic start: no frame
// steps the sim before a spec does); `?faults=a,b` plants the defects named in SIM_FAULT_NAMES.
/// <reference types="vite/client" />
import * as THREE from "three";
import { attachDom, type Actions, type Runtime, type InputTape } from "@rpg/runtime";
import type { Rapier } from "@rpg/collision";
import type { Invariant } from "@rpg/harness";
import type { EventBus } from "@rpg/kernel";
import type { CoreReport, GameEvents } from "../core";
import { createSimHost, type SceneName, type SimSave } from "./host";
import { LAB, SIM_FAULTS } from "./scene";
import { createSimView } from "./view";
import { VALLEY_FAULT_NAMES, setValleyFault, valleyHandle, valleyInvariants, valleyScene, valleyView, type ValleyFaultName } from "../valley";
import { mountValleyCamera } from "../valley/camera";
import { mountValleyLook } from "../valley/look";
import type { LookSource } from "../camera";
import type { SpikeContent } from "../content";
import type { RigSnapshot } from "@rpg/camera";
import type { MotionReport } from "@rpg/figure";
import type { ValleyRig } from "../valley/scene";
import { dojoMount, type DojoMount } from "../combat";
import { ARENA_FAULT_NAMES, arenaHandle, arenaInvariants, arenaState, fightHandle, fightInvariants, fightSummary, mountArena, setArenaFault, arenaScene, type ArenaFaultName } from "../enemies";
import { HERO_ID, type Fight } from "../enemies/fight";
import { valleyFight } from "../valley/fight";
import { heroPoses, mountValleyFightView, type ValleyFightView } from "../valley/fight-view";
import { mountTargeting, type Targeting } from "../targets/targeting";
import { stickToWorld } from "@rpg/controller";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { LowPolyLook, LowPolySceneLook } from "@rpg/look";
import type { ArenaView } from "../enemies/view";
import { mountRagdollLab, ragdollSceneFor } from "../bodies";
import { applyWorldFaultSwitches, worldParts } from "../world";
import { teleportHero } from "../world/teleport";
import { hudWorld, type HudWorld } from "../hud/scene";
import { placesMount, type PlacesMount } from "../places";
import { mountSkyLab, skySceneFor } from "../sky";
import { CAPTURE_RAIN } from "../sky/view";
import { mountThingsView, thingsHandle, thingsInvariants, thingsLab, thingsSummary, type InteractPromptState, type Things, type ThingsView } from "../things";
import { marketLab, mountRpgView, rpgHandle, rpgInvariants, setRpgFaults, type Rpg, type RpgUi, type RpgView } from "../rpg";
import { applyTownFaultSwitches, mountTown } from "../town";
import { hashState } from "@rpg/kernel";
import type { SimTools } from "../tools/seam";
import { applyStoryFaultSwitches, storySceneFor, type StoryMount } from "../story";
import { compiledScripts } from "../story/scripts";
import { joinedWorld } from "../joined";
import type { MetaLayer } from "../meta/module";
import type { Figure } from "@rpg/figure";
import type { ValleyCamera } from "../valley/camera";

export type { SimSave } from "./host";

/** Every planted defect the sim owns: the lab's (A1.3) and the valley's (A2.2, A2.3). */
export const SIM_FAULT_NAMES = ["mathRandom", "restoreWithoutSnapshot", ...VALLEY_FAULT_NAMES, ...ARENA_FAULT_NAMES] as const;
export type SimFaultName = (typeof SIM_FAULT_NAMES)[number];
/** The planted defects to switch, by name. */
export type SimFaults = Partial<Record<SimFaultName, boolean>>;

/** Where the tuning comes from (the content registry's tuning table). */
export interface TuningSource {
  get(id: string): number | undefined;
  ids(): readonly string[];
}

const SCENARIOS = import.meta.glob<{ scene?: SceneName }>("../harness/scenarios/*.json", { eager: true, import: "default" });

/** The scene a page boots: `?scene=`, else the `?scenario=` fixture's own, else the streamed valley (`world`: the
 *  default since phase C; phase A's test valley with its course is `?scene=valley`). */
export function sceneOf(params: URLSearchParams): SceneName {
  const s = params.get("scene");
  if (s === "lab" || s === "valley" || s === "dojo" || s === "arena" || s === "ragdoll" || s === "world" || s === "places" || s === "sky" || s === "things" || s === "market" || s === "town" || s === "hud" || s === "story") return s;
  // the camera lab (src/camera/lab.ts) brings its own world and hides the lab's pen, which still steps under it
  // so do the figure lab (src/figure/lab.ts, its own world and scene; the lab's sim steps offstage)
  // and the targets lab (src/targets/lab.ts, phase B's targeting and impact checks)
  // and the talk lab (src/talk/lab.ts, capability round 1: the conversation and scene-player units)
  // and the creatures lab (src/creatures/lab.ts, IMP-0059: the creature presets on the four-legged motion)
  if (s === "camera" || s === "figure" || s === "targets" || s === "talk" || s === "creatures") return "lab";
  if (s) throw new Error(`unknown scene "${s}" (have lab, valley, dojo, arena, ragdoll, world, places, sky, things, market, town, hud, story, camera, figure, targets, talk, creatures)`);
  const sc = params.get("scenario");
  const fixture = sc ? Object.entries(SCENARIOS).find(([k]) => k.endsWith(`/${sc}.json`))?.[1] : undefined;
  return fixture?.scene ?? "world";
}

export interface SimMount {
  /** The scene this page runs. */
  scene: SceneName;
  /** Draw a frame through the valley's low-poly look, when it has one (else the page renders the scene itself). */
  render: (() => void) | null;
  /** Keys for `window.__rpg`. */
  handle: {
    /** The deterministic state hash (64-bit hex). */
    hash(): string;
    /** The hash's parts (step, kernel, physics, props, rng), to find which diverged. */
    hashParts(): ReturnType<ReturnType<typeof createSimHost>["hashParts"]>;
    /** A JSON-safe save of the whole sim. */
    save(): SimSave;
    restore(save: SimSave): void;
    /** Record what each sim step reads from the input, from the next step on. */
    record(): void;
    stopRecording(): InputTape | null;
    /** Replay a tape (keyed by absolute step, so it works after a restore). */
    replay(tape: InputTape): void;
    stopReplay(): void;
    /** Injected input on sim time: override the `move` stick (null clears), hold a control, tap or hold an action. */
    input: {
      stick(x: number, y: number): void;
      clearStick(): void;
      hold(control: string, seconds: number): void;
      tap(action: string): void;
      holdAction(action: string, seconds: number): void;
    };
    /** Turn planted defects on or off. */
    faults(f: SimFaults): void;
    /** The host step's CPU time (kernel systems and the Rapier step). Headless numbers are counts, not frame times. */
    simStats(reset?: boolean): ReturnType<ReturnType<typeof createSimHost>["stepStats"]> & { bodies: number };
    /** The one kernel game's report (gate A1.4). */
    kernel(): CoreReport;
  } & Partial<ReturnType<typeof valleyHandle>> & {
    /** The ragdoll lab (`?scene=ragdoll`, phase B5): the soak's report, scripted defeats, hits and moves, poses. */
    ragdoll?: ReturnType<typeof mountRagdollLab>["handle"];
    /** The sky lab (`?scene=sky`, phase C3): the shots, the clock, the weather, the look, the perf tour. */
    sky?: ReturnType<typeof mountSkyLab>["handle"];
  } & {
    /** The Wanderer in the valley (A3 on the controller): its motion's report, and a fresh drift measurement. */
    wanderer?: { report(): MotionReport; resetDrift(): void };
    /** Combat in the dojo (phase B, gates B1 and B2; src/combat). */
    combat?: ReturnType<DojoMount["handle"]>;
    /** The valley's fight (phase B joined; src/valley/fight.ts). */
    fight?: ReturnType<typeof valleyFightHandle>;
    /** The streamed valley (phase C, `?scene=world`; src/world/). */
    world?: ReturnType<typeof worldParts>["handle"];
    /** The places lab (`?scene=places`, gate C4; src/places/). */
    places?: ReturnType<PlacesMount["handle"]>;
    /** The things lab (`?scene=things`, gate C5; src/things/). */
    things?: ReturnType<typeof thingsHandle>;
    /** The market lab's rpg layer (`?scene=market`, phase D1; src/rpg/). */
    rpg?: ReturnType<typeof rpgHandle>;
    /** Phase D1's town (`?scene=town`; src/town/): the townsfolk, their routines, the cutscenes, the crowd's LOD. */
    town?: ReturnType<typeof mountTown>["handle"];
    /** The story (`?scene=story`, gate D1.3; src/story/). */
    story?: StoryMount["handle"];
  };
  /** The story scene's mount (`?scene=story`): main.ts mounts the dialogue box and the quest log on it. */
  story?: StoryMount;
  /** Phase E1: the meta layer (achievements, the codex) on the joined valley; main.ts mounts its screens over it. */
  meta?: MetaLayer;
  /** Phase E1: what photo mode needs of the joined valley's view (src/meta/photo.ts). */
  photo?: PhotoHooks;
  /** Whether the scene's fight has an enemy alert to the hero (main.ts pushes the combat input context then). */
  engaged?: () => boolean;
  /** Phase D2 (src/audio/): the fight's fighters now (the hero's `level` is null), for growls on alert and swings. */
  fighters?: () => { id: number; pos: [number, number, number]; alive: boolean; level: string | null }[];
  /** Phase D2: how much a wall stands between two points (0 open, 1 behind a building: a ray on the solid layer). */
  occlusion?: (from: readonly [number, number, number], to: readonly [number, number, number]) => number;
  /** The things lab's things (phase C, gate C5): main.ts mounts the interaction prompt on them. */
  things?: () => Things;
  /** Where main.ts reports the prompt it mounted, for the handle. */
  setPrompt?: (state: () => InteractPromptState) => void;
  /** The market lab's rpg layer (phase D1): main.ts mounts its screens on the UI and reports them back. */
  rpg?: () => Rpg | null;
  setRpgUi?: (ui: RpgUi) => void;
  /** The scene's invariants for the harness's watch (the valley's hero checks; none in the lab). */
  invariants: Invariant[];
  /** Capture only (the harness's `settle()`, IMP-0019): settle the scene's random view (the valley's rain) from
   *  `random`; null where the scene has nothing to settle. */
  settle?: (random: () => number) => unknown;
  /** The kernel game's event bus (phase B: the combat events; the feedback and the labs listen here). */
  events: EventBus<GameEvents>;
  /** The hud lab's parts (phase D2, `?scene=hud`): the world, the discovery and the kernel's dispatch, for the HUD. */
  hud?: { world: HudWorld; dispatch(action: string, payload: unknown): void };
  /** Write NaN into the scene's driven body (the harness's `nanState` planted defect). */
  plantNaN(): void;
  /** A summary for `state()`. */
  state(): Record<string, unknown>;
  /** Phase E: what the dev tools read (src/tools/seam.ts); absent in the arena. */
  tools?: SimTools;
}

/** What photo mode needs of the valley's view: the look (its post chain for the depth of field), the hero's figure (its
 *  pose), the follow camera (its profile stack), the sky's shown hour, the ground. */
export interface PhotoHooks {
  look(): LowPolyLook | null;
  figure(): Figure | null;
  camera: ValleyCamera;
  /** Show an hour on the sky in place of the clock's (null: the clock's); the sim's clock is untouched. */
  setViewHours(h: number | null): void;
  /** The sim clock's hour, and the hour the sky last drew. */
  simHours(): number;
  shownHours(): number;
  /** The hero's feet, and its controller's mode (ground, air, glide, mounted…: the tutorials' `play.airborne`). */
  hero(): { x: number; y: number; z: number };
  heroMode(): string;
  ground(x: number, z: number): number;
}

export function mountSim(o: {
  rapier: Rapier;
  runtime: Runtime;
  scene: THREE.Object3D;
  /** The valley's follow camera drives it (with `content`; src/valley/camera.ts). */
  camera?: THREE.PerspectiveCamera;
  content?: SpikeContent;
  /** Look input for the camera: the input's `look` action. */
  look?: LookSource;
  /** The camera's settings (A4): a setting's value now. */
  setting?: (id: string) => unknown;
  /** The valley in the low-poly look with the Wanderer (A3; src/valley/look.ts), with `camera` and `content`. */
  renderer?: THREE.WebGLRenderer;
  /** The look's tier ("low" | "high"). */
  tier?: string;
  tuning: TuningSource;
  search?: string;
  actions?: Actions;
}): SimMount {
  const params = new URLSearchParams(o.search ?? (typeof location !== "undefined" ? location.search : ""));
  if (params.has("paused")) o.runtime.paused = true;
  const name = sceneOf(params);
  const named = (params.get("faults") ?? "").split(",").filter(Boolean);
  // valley faults are read when its world is built, so set them first
  for (const n of named) if ((VALLEY_FAULT_NAMES as string[]).includes(n)) setValleyFault(null, n as ValleyFaultName, true);
  // phase B's dojo (`?scene=dojo`, src/combat/): the valley's machinery on flat ground with combat and its dummies
  if (name === "dojo" && !o.content) throw new Error("the dojo needs the content");
  const dojo = name === "dojo" && o.content ? dojoMount({ content: o.content, runtime: o.runtime }) : null;
  // the B4 arena's and the valley's fights (src/enemies/): their faults are read when their worlds are built, so set
  // them first too
  for (const n of named) if ((ARENA_FAULT_NAMES as string[]).includes(n)) setArenaFault(null, n as ArenaFaultName, true);
  const difficulty = params.get("difficulty") ?? (o.setting?.("accessibility.difficulty") as string | undefined);
  // phase B joined: combat in the default valley (src/valley/fight.ts): its camps and the Warden through the director;
  // `?encounter=off` leaves them out
  // phase C: the streamed valley from the layout (`?scene=world`, src/world/): the valley's machinery and the fight on
  // @rpg/world's chunks
  // phase D joined: the streamed valley is the joined game at `?scene=world`, `town` and `hud` too: the rpg layer, the
  // town, discovery (the hud stream's module: the HUD, the map and the world-space UI mount over it in main.ts)
  const joined = name === "world" || name === "town" || name === "hud" || name === "story";
  // phase D joined (src/joined.ts): the streamed valley with the rpg layer, discovery (the HUD, the map and the
  // world-space UI mount over it in main.ts), the story (a world addon; `?story=off` leaves it out) and the town wrapped
  // round it, on one condition language; their planted defects are read as they're built, so set them first
  if (joined && o.content) {
    const q = `?${params.toString()}`;
    applyWorldFaultSwitches(q);
    applyTownFaultSwitches(q);
    applyStoryFaultSwitches(q);
  }
  const joinedW = joined && o.content ? joinedWorld({ content: o.content, runtime: o.runtime, params, ...(difficulty ? { difficulty } : {}), scripts: compiledScripts() }) : null;
  const hudW = joinedW?.hud ?? null;
  const worldM = joinedW?.world ?? null;
  const townM = joinedW?.town ?? null;
  const fighting = name === "valley" && o.content ? valleyFight({ content: o.content, runtime: o.runtime, ...(difficulty ? { difficulty } : {}), encounter: params.get("encounter") !== "off" }) : worldM ? { variant: worldM.variant, fight: worldM.fight } : null;
  // phase C4: the places lab (`?scene=places`, src/places/): the valley's machinery on the lab's town, water and rooms
  const places = name === "places" && o.content ? placesMount({ content: o.content, runtime: o.runtime, tier: o.tier ?? "high", search: `?${params.toString()}` }) : null;
  // phase C5: the things lab (src/things/), on the valley's machinery with the joined fight
  const thingsMount = name === "things" && o.content ? thingsLab({ content: o.content, runtime: o.runtime }) : null;
  // phase D1: the market lab (src/rpg/), the things lab's machinery with the rpg layer; its content's planted defects
  // are read with the content, so set them first
  setRpgFaults(named);
  const market = name === "market" && o.content ? marketLab({ content: o.content, runtime: o.runtime }) : null;
  const valley = name === "valley" || joined || dojo || places || thingsMount || market ? valleyScene(o.tuning.get, o.tuning.ids(), dojo?.variant ?? places?.variant ?? fighting?.variant ?? thingsMount?.variant ?? market?.variant) : null;
  const fightOf = (): Fight | null => (arena ? arena.rig().fight : fighting ? fighting.fight() : thingsMount ? thingsMount.fight() : market ? market.fight() : null);
  const arena = name === "arena" && o.content ? arenaScene(o.tuning.get, o.tuning.ids(), o.content, { ...(difficulty ? { difficulty } : {}), director: params.get("director") !== "off", runtime: o.runtime }) : null;
  // phase B5: the ragdoll lab (src/bodies/), its own world and modules
  const ragdoll = name === "ragdoll" && o.content ? ragdollSceneFor(o.content) : null;
  // phase C3: the sky lab (src/sky/), the clock and the weather on a stand-in world
  const skyScene = name === "sky" && o.content ? skySceneFor(o.content) : null;
  const host = createSimHost(o.rapier, o.runtime, valley ? valley.def : arena ? arena.def : ragdoll ? ragdoll.def : skyScene ? skyScene.def : LAB, o.actions);
  const rig = (): ValleyRig => valley!.rig();
  const faults = (f: SimFaults): void => {
    for (const [k, on] of Object.entries(f) as [SimFaultName, boolean | undefined][]) {
      if (on === undefined) continue;
      if (k === "mathRandom") SIM_FAULTS.mathRandom = on;
      else if (k === "restoreWithoutSnapshot") host.collision.faults.restoreWithoutSnapshot = on;
      else if ((ARENA_FAULT_NAMES as string[]).includes(k)) setArenaFault(fightOf(), k as ArenaFaultName, on);
      else setValleyFault(valley ? rig() : null, k as ValleyFaultName, on);
    }
  };
  faults(Object.fromEntries(named.filter((k) => (SIM_FAULT_NAMES as readonly string[]).includes(k)).map((k) => [k, true])) as SimFaults);

  // live keys for the user; tests inject through `input` or a tape instead (with the game's input passed in,
  // src/input wires the page; this is the fallback for a page without it)
  if (!o.actions && typeof window !== "undefined" && typeof document !== "undefined") attachDom(host.actions.controls, window, document.body);

  const draw = valley ? valleyView(o.scene, host) : arena || ragdoll || skyScene ? () => {} : createSimView(o.scene, host.setup.bodies, host.collision);
  const skyLab =
    skyScene && o.content && o.renderer && o.camera && o.scene instanceof THREE.Scene
      ? mountSkyLab({ lab: skyScene, runtime: o.runtime, renderer: o.renderer, scene: o.scene, camera: o.camera, content: o.content, tier: o.tier ?? "high", events: host.game.events, dispatch: (a, p) => host.game.dispatch(a, p) })
      : null;
  const ragdollLab = ragdoll && o.content ? mountRagdollLab({ lab: ragdoll, runtime: o.runtime, ...(o.renderer ? { renderer: o.renderer } : {}), scene: o.scene, ...(o.camera ? { camera: o.camera } : {}), content: o.content, tier: o.tier ?? "high" }) : null;
  // the valley's follow camera (gate A2.4) on the hero; its yaw is the one the sim's stick is relative to
  // the low-poly look and the Wanderer (A3) on the valley's main scene, when there is a renderer to draw it
  // the camera before the look: the impact director (phase B) shakes and kicks its rig. While a lock-on holds (the
  // valley's targeting, phase B3), look input turns into flicks that switch targets instead of orbiting the camera.
  let targeting: Targeting | null = null;
  let lockLook: LookSource | null = null;
  const look = o.look;
  const lookSource: LookSource | undefined = look ? (dt) => (lockLook ?? look)(dt) : undefined;
  const cam = valley && o.camera && o.content ? mountValleyCamera({ runtime: o.runtime, camera: o.camera, content: o.content, rig, ...(lookSource ? { look: lookSource } : {}), ...(o.setting ? { setting: o.setting } : {}) }) : null;
  // the look, the Wanderer and (phase B) the hero's poses; the fight's view is mounted inside its warm-up, so every
  // program the impact director draws compiles before ready
  let fightView: ValleyFightView | null = null;
  const world = worldM ? worldParts(worldM, { runtime: o.runtime, dispatch: (a, p) => host.game.dispatch(a, p) }) : null;
  // the story's handle and the UI's reads over the joined world's story, the town's cutscenes under it
  const storyM = joinedW?.story && o.content ? storySceneFor({ content: o.content, story: joinedW.story, npcs: joinedW.npcs, scenes: joinedW.scenes((a, p) => host.game.dispatch(a, p)) }) : null;
  if (storyM && worldM) storyM.attach({ dispatch: (a, p) => host.game.dispatch(a, p), events: host.game.events, runtime: o.runtime, world: worldM });
  // phase C joined: the sky in the default scene, made before the look (its dome and shaft pass are the look's options)
  const worldSky = world && o.content && o.renderer && o.camera && o.scene instanceof THREE.Scene ? world.skyView({ content: o.content, scene: o.scene as THREE.Scene, camera: o.camera, renderer: o.renderer, tier: o.tier ?? "high" }) : null;
  let thingsView: ThingsView | null = null;
  let rpgView: RpgView | null = null;
  let rpgUi: RpgUi | null = null;
  let promptState: (() => InteractPromptState) | null = null;
  const town = townM ? mountTown({ town: townM, runtime: o.runtime, game: host.game, events: host.game.events, hashed: host.setup.hashed, physicsHash: () => host.collision.hash(), rngHash: () => hashState(host.rng.snapshot()), dispatch: (a, p) => host.game.dispatch(a, p), tier: o.tier ?? "high", settleView: () => world?.view()?.prime() }) : null;
  const scene3 = o.scene instanceof THREE.Scene ? o.scene : null;
  const dressed =
    valley && o.camera && o.content && o.renderer && scene3
      ? mountValleyLook({
          runtime: o.runtime,
          renderer: o.renderer,
          scene: scene3,
          camera: o.camera,
          content: o.content,
          rig,
          tier: o.tier ?? "high",
          view: o.scene.getObjectByName("sim-test-scene") ?? o.scene,
          ...(dojo ? { acting: dojo.acting, poses: (a: number) => dojo.heroPoses(a) } : {}),
          ...(fighting ? { acting: () => fighting.fight().engaged(), poses: heroPoses(() => fighting.fight(), o.runtime) } : {}),
          ...(thingsMount ? { poses: heroPoses(() => thingsMount.fight(), o.runtime) } : {}),
          ...(market ? { acting: () => market.fight().engaged(), poses: heroPoses(() => market.fight(), o.runtime) } : {}),
          ...(worldSky ? { sky: worldSky } : {}),
          warm: (lookNow, hero) => {
            const setting = o.setting ? { setting: o.setting } : {};
            // phase C: the world's places and view tier, primed here so its chunks compile with the rest before ready;
            // its things' view after the fight's
            if (world && worldM && o.content && o.renderer && o.camera) {
              const sceneLook = o.content.registry.get("looks", "valley-day") as unknown as LowPolySceneLook;
              const wv = world.mountView({ look: lookNow, sceneLook, renderer: o.renderer, scene: scene3, camera: o.camera, valleyCamera: cam, tier: o.tier ?? "high" });
              fightView = mountValleyFightView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, fight: () => worldM.fight(), rig, hero, events: host.game.events, camera: cam?.rig ?? null, ...setting });
              const probe = (x: number, z: number, fromY: number): number | null => rig().collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["terrain", "solid"] })?.point.y ?? null;
              thingsView = mountThingsView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, things: () => worldM.things(), fight: () => worldM.fight(), collision: () => rig().collision, probe, shown: (id) => world.shown(id), walker: false });
              const clearFight = fightView.clearWarm;
              const clearThings = thingsView.clearWarm;
              // phase D1: the townsfolk's figures and the cutscenes' puppets, built in the warm-up
              const tv = town ? town.mountView({ scene: scene3, camera: o.camera, look: lookNow, content: o.content, renderer: o.renderer, ...(cam ? { snapCamera: () => cam.rig.snap() } : {}) }) : null;
              // phase D joined: the rpg layer's view (the forge and the pot where the layout puts them, on the ground
              // whether or not their chunk has loaded; the scarf's length; the level-up moment)
              rpgView = mountRpgView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, rpg: () => worldM.rpg(), fight: () => worldM.fight(), collision: () => rig().collision, probe: (x, z) => worldM.world.heightAt(x, z), hero, events: host.game.events, notice: false });
              const clearRpg = rpgView.clearWarm;
              return () => {
                clearFight();
                clearThings();
                wv.clearWarm();
                tv?.clearWarm();
                clearRpg();
              };
            }
            if (places && o.renderer) return void places.mountView({ scene: scene3, look: lookNow, hero, camera: cam, renderer: o.renderer });
            if (dojo) return dojo.mountView({ scene: scene3, look: lookNow, hero, events: host.game.events, rig: cam?.rig ?? null, ...setting });
            if (fighting && o.content) {
              fightView = mountValleyFightView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, fight: () => fighting.fight(), rig, hero, events: host.game.events, camera: cam?.rig ?? null, ...setting });
              return fightView.clearWarm;
            }
            if (market && o.content) {
              // the market lab (phase D1): the fight's view, the things' and the rpg layer's (stations, townsfolk
              // stand-ins, the scarf's length, the level-up moment)
              fightView = mountValleyFightView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, fight: () => market.fight(), rig, hero, events: host.game.events, camera: cam?.rig ?? null, ...setting });
              const probe = (x: number, z: number, fromY: number): number | null => rig().collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["terrain", "solid"] })?.point.y ?? null;
              thingsView = mountThingsView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, things: () => market.things(), fight: () => market.fight(), collision: () => rig().collision, probe, walker: false });
              rpgView = mountRpgView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, rpg: () => market.rpg(), fight: () => market.fight(), collision: () => rig().collision, probe, hero, events: host.game.events });
              const clears = [fightView.clearWarm, thingsView.clearWarm, rpgView.clearWarm];
              return () => {
                for (const c of clears) c();
              };
            }
            if (thingsMount && o.content) {
              // the fight's view (the impact director: a blow on a crate flashes, holds and shakes) and the things'
              fightView = mountValleyFightView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, fight: () => thingsMount.fight(), rig, hero, events: host.game.events, camera: cam?.rig ?? null, ...setting });
              const probe = (x: number, z: number, fromY: number): number | null => rig().collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["terrain", "solid"] })?.point.y ?? null;
              thingsView = mountThingsView({ runtime: o.runtime, scene: scene3, look: lookNow, content: o.content, things: () => thingsMount.things(), fight: () => thingsMount.fight(), collision: () => rig().collision, probe });
              const clearFight = fightView.clearWarm;
              const clearThings = thingsView.clearWarm;
              return () => {
                clearFight();
                clearThings();
              };
            }
            return undefined;
          },
        })
      : null;
  // the valley's targeting (gate B3): the soft pick, and with the lock-on setting the lock, switched by flicks; a hero's
  // attack asks it what to turn to when it starts (the fight's `target`)
  if (fighting && o.content && o.actions) {
    const fight = (): Fight => fighting.fight();
    const actions = o.actions;
    targeting = mountTargeting({
      runtime: o.runtime,
      actions,
      content: o.content,
      setting: (id) => o.setting?.(id),
      from: () => {
        const h = rig().hero();
        const st = stickToWorld(actions.stick("move"), rig().control().cameraYaw);
        return { pos: [h.feet.x, h.feet.y, h.feet.z], facing: h.yaw, input: [st.x, st.z] };
      },
      candidates: () => fight().state().enemies.map((e) => ({ id: e.id, pos: e.pos, alive: e.alive, priority: fight().content.kinds[e.kind]!.phases.length ? 1 : 0 })),
      lineOfSight: (a, b) => {
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const dz = b[2] - a[2];
        const d = Math.hypot(dx, dy, dz);
        if (d < 1e-6) return true;
        const hit = rig().collision.castRay({ x: a[0], y: a[1], z: a[2] }, { x: dx / d, y: dy / d, z: dz / d }, d, { layers: ["terrain", "solid"] });
        return !hit || hit.distance >= d - 0.05;
      },
      camera: cam,
    });
    if (look) lockLook = targeting.look(look);
    fight().target = () => targeting!.current();
  }
  o.runtime.on("late", () => draw(), { name: "sim: pose view" });

  const common = {
    hash: () => host.hash(),
    hashParts: () => host.hashParts(),
    // the camera rig's yaw, pitch offset and profile requests ride along (a view's state: not in the hash)
    save: (): SimSave => (cam ? { ...host.save(), camera: cam.rig.snapshot() } : host.save()),
    restore: (s: SimSave) => {
      host.restore(s);
      if (cam && s.camera) {
        cam.rig.restore(s.camera as RigSnapshot);
        cam.rig.snap();
      }
    },
    record: () => host.actions.record(),
    stopRecording: () => host.actions.stopRecording(),
    replay: (t: InputTape) => host.actions.replay(t),
    stopReplay: () => host.actions.stopReplay(),
    input: {
      stick: (x: number, y: number) => host.actions.setStick("move", { x, y }),
      clearStick: () => host.actions.setStick("move", null),
      hold: (control: string, seconds: number) => host.actions.controls.hold(control, seconds),
      tap: (action: string) => host.actions.tapAction(action),
      holdAction: (action: string, seconds: number) => host.actions.holdAction(action, seconds),
    },
    faults,
    simStats: (reset?: boolean) => ({ ...host.stepStats(reset), bodies: host.collision.ids().length }),
    kernel: () => host.kernel(),
  };
  const recording = () => ({ replaying: host.actions.replaying, recording: host.actions.recording });
  if (arena) {
    // the B4 arena (src/enemies/): its own view, handle keys, invariants and state
    const arenaView: ArenaView | null =
      o.camera && o.content && o.renderer && o.scene instanceof THREE.Scene
        ? mountArena({ runtime: o.runtime, renderer: o.renderer, scene: o.scene, camera: o.camera, content: o.content, rig: () => arena.rig(), tier: o.tier ?? "high", navDebug: params.has("navDebug"), events: host.game.events, ...(o.setting ? { setting: o.setting } : {}) })
        : null;
    return {
      scene: name,
      render: arenaView ? () => arenaView.render() : null,
      handle: { ...common, ...arenaHandle(() => arena.rig(), host, o.runtime, () => arenaView) },
      invariants: arenaInvariants(() => arena.rig()),
      events: host.game.events,
      plantNaN: () => void (arena.rig().hero().feet.x = Number.NaN),
      state: () => ({ scene: name, step: o.runtime.clock.step, bodies: host.collision.ids().length, arena: arenaState(arena.rig()), ...(arenaView ? { view: arenaView.state() } : {}), ...recording() }),
      engaged: () => arena.rig().fight.engaged(),
    };
  }
  return {
    scene: name,
    render: dressed ? () => dressed.render() : (ragdollLab?.render ?? skyLab?.render ?? null),
    handle: valley
      ? {
          ...common,
          ...valleyHandle(rig, host, o.runtime),
          ...(dressed ? { wanderer: { report: () => dressed.hero.motion.report(), resetDrift: () => dressed.hero.motion.resetDrift() } } : {}),
          ...(dojo ? { combat: dojo.handle() } : {}),
          ...(world ? { world: world.handle } : {}),
          ...(places ? { places: places.handle() } : {}),
          ...(fighting ? { fight: valleyFightHandle({ fight: () => fighting.fight(), runtime: o.runtime, view: () => fightView, targeting: () => targeting, host, rig, renderer: o.renderer ?? null, scene: scene3, look: () => dressed?.look ?? null, tier: o.tier ?? "high" }) } : {}),
          ...(thingsMount ? { things: thingsHandle({ things: () => thingsMount.things(), fight: () => thingsMount.fight(), rig, runtime: o.runtime, view: () => thingsView, prompt: () => promptState?.() ?? null }) } : {}),
          ...(market && o.content
            ? {
                things: thingsHandle({ things: () => market.things(), fight: () => market.fight(), rig, runtime: o.runtime, view: () => thingsView, prompt: () => promptState?.() ?? null }),
                rpg: rpgHandle({ rpg: () => market.rpg(), fight: () => market.fight(), things: () => market.things(), rig, runtime: o.runtime, conditions: market.conditions, standing: (f, v) => void market.standing.change(f, v - market.standing.get(f), { kind: "handle" }), content: o.content, view: () => rpgView, ui: () => rpgUi }),
              }
            : {}),
          ...(worldM && o.content
            ? {
                things: thingsHandle({ things: () => worldM.things(), fight: () => worldM.fight(), rig, runtime: o.runtime, view: () => thingsView, prompt: () => promptState?.() ?? null }),
                rpg: rpgHandle({ rpg: () => worldM.rpg(), fight: () => worldM.fight(), things: () => worldM.things(), rig, runtime: o.runtime, conditions: worldM.conditions,
                standing: (f, v) => {
                  // the story's standing (one source: the shops' discount reads its tier's perk)
                  const st = storyM?.layer().story.standing;
                  if (!st) throw new Error("rpg: no story here to hold the standing (?story=off)");
                  st.change(f, v - st.get(f), { kind: "handle" });
                },
                content: o.content, view: () => rpgView, ui: () => rpgUi, teleport: (x, z, yaw) => teleportHero(worldM, x, z, yaw) }),
              }
            : {}),
          ...(town ? { town: town.handle } : {}),
          ...(storyM ? { story: storyM.handle } : {}),
        }
      : ragdollLab
        ? { ...common, ragdoll: ragdollLab.handle }
        : skyLab
          ? { ...common, sky: skyLab.handle }
          : common,
    invariants: valley
      ? [
          ...valleyInvariants(rig, o.runtime, { paused: () => host.game.paused().length > 0 }),
          ...(dojo ? dojo.invariants() : []),
          ...(fighting ? fightInvariants(() => fighting.fight(), (x, z) => rig().terrainAt(x, z)) : []),
          ...(world && worldM ? [...world.invariants(), ...thingsInvariants(() => worldM.things(), () => worldM.fight(), (x, z) => rig().terrainAt(x, z)), ...rpgInvariants(() => worldM.rpg())] : []),
          ...(town ? town.invariants() : []),
          ...(storyM ? storyM.invariants() : []),
          ...(thingsMount ? [...fightInvariants(() => thingsMount.fight(), (x, z) => rig().terrainAt(x, z)), ...thingsInvariants(() => thingsMount.things(), () => thingsMount.fight(), (x, z) => rig().terrainAt(x, z))] : []),
          ...(market ? [...fightInvariants(() => market.fight(), (x, z) => rig().terrainAt(x, z)), ...thingsInvariants(() => market.things(), () => market.fight(), (x, z) => rig().terrainAt(x, z)), ...rpgInvariants(() => market.rpg())] : []),
        ]
      : (ragdollLab?.invariants ?? skyLab?.invariants ?? []),
    ...(fighting ? { engaged: () => fighting.fight().engaged() } : {}),
    ...(fighting
      ? {
          fighters: () => {
            const st = fighting.fight().state();
            const h = rig().hero().feet;
            return [{ id: st.heroId, pos: [h.x, h.y, h.z] as [number, number, number], alive: true, level: null }, ...st.enemies.map((e) => ({ id: e.id, pos: [e.pos[0], e.pos[1], e.pos[2]] as [number, number, number], alive: e.alive, level: e.percept.level as string }))];
          },
        }
      : {}),
    ...(valley
      ? {
          occlusion: (a: readonly [number, number, number], b: readonly [number, number, number]) => {
            const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
            const len = Math.hypot(d[0]!, d[1]!, d[2]!);
            if (len < 0.5) return 0;
            const dir = { x: d[0]! / len, y: d[1]! / len, z: d[2]! / len };
            return rig().collision.castRay({ x: a[0], y: a[1], z: a[2] }, dir, len - 0.25, { layers: ["solid"] }) ? 1 : 0;
          },
        }
      : {}),
    ...(thingsMount ? { things: () => thingsMount.things(), setPrompt: (st: () => InteractPromptState) => void (promptState = st) } : {}),
    ...(market ? { things: () => market.things(), setPrompt: (st: () => InteractPromptState) => void (promptState = st), engaged: () => market.fight().engaged(), rpg: () => market.rpg(), setRpgUi: (u: RpgUi) => void (rpgUi = u) } : {}),
    ...(worldM ? { things: () => worldM.things(), setPrompt: (st: () => InteractPromptState) => void (promptState = st), rpg: () => worldM.rpg(), setRpgUi: (u: RpgUi) => void (rpgUi = u) } : {}),
    ...(storyM ? { story: storyM } : {}),
    ...(joinedW ? { meta: joinedW.meta.layer() } : {}),
    ...(joinedW && worldM && world && cam
      ? {
          photo: {
            look: () => dressed?.look ?? null,
            figure: () => dressed?.hero.figure ?? null,
            camera: cam,
            setViewHours: (h: number | null) => world.setViewHours(h),
            simHours: () => worldM.sky().hours(),
            shownHours: () => world.sky()?.report().hours ?? worldM.sky().hours(),
            hero: () => {
              const f = rig().hero().feet;
              return { x: f.x, y: f.y, z: f.z };
            },
            heroMode: () => rig().hero().mode,
            ground: (x: number, z: number) => worldM.world.heightAt(x, z),
          } satisfies PhotoHooks,
        }
      : {}),
    events: host.game.events,
    ...(hudW ? { hud: { world: hudW, dispatch: (a: string, p: unknown) => host.game.dispatch(a, p) } } : {}),
    tools: { game: host.game, rig: valley ? rig : null, camera: cam, world: worldM, view: world ? () => world.view() : null },
    ...(world ? { settle: (random: () => number) => world.sky()?.settle({ seconds: CAPTURE_RAIN.seconds, step: CAPTURE_RAIN.step, random }) ?? null } : {}),
    plantNaN: valley ? () => void (rig().hero().feet.x = Number.NaN) : ragdollLab ? ragdollLab.plantNaN : skyScene ? () => void (skyScene.sky().state().clock.hours = Number.NaN) : () => host.collision.setPose("pusher", { x: Number.NaN, y: Number.NaN, z: Number.NaN }),
    state: valley
      ? () => {
          const r = rig();
          const h = r.hero();
          return {
            scene: name,
            step: o.runtime.clock.step,
            bodies: host.collision.ids().length,
            hero: { x: h.feet.x, y: h.feet.y, z: h.feet.z, mode: h.mode, speed: Math.hypot(h.vel.x, h.vel.z), grounded: h.grounded, surface: h.surface, water: h.water, riding: h.riding },
            // the stub mount's feet on the A2.3 course; null where there is none (the world, decision 0084)
            mount: r.mount() ? { ...r.mount()!.feet } : null,
            owner: r.handoff.owner(r.control().handoff).id,
            cameraProfile: r.control().profile,
            ...(cam ? { camera: cam.state() } : {}),
            ...(dressed ? { figure: dressed.state() } : {}),
            ...(fighting ? { fight: fightSummary(fighting.fight()), lock: targeting?.state() ?? null } : {}),
            ...(world && worldM ? { world: world.state(), things: thingsSummary(worldM.things()), rpg: { level: worldM.rpg().progress().level, gold: worldM.rpg().state().wallet.balance, glyphs: worldM.rpg().glyphs() } } : {}),
            ...(town ? { town: town.state() } : {}),
            ...(storyM ? { story: storyM.summary() } : {}),
            ...(places ? { places: places.state() } : {}),
            ...(thingsMount ? { things: thingsSummary(thingsMount.things()) } : {}),
            ...(market ? { things: thingsSummary(market.things()), rpg: { level: market.rpg().progress().level, gold: market.rpg().state().wallet.balance, glyphs: market.rpg().glyphs() } } : {}),
            ...recording(),
          };
        }
      : ragdollLab
        ? () => ({ scene: name, step: o.runtime.clock.step, bodies: host.collision.ids().length, ...ragdollLab.state(), ...recording() })
        : skyLab
          ? () => ({ scene: name, step: o.runtime.clock.step, bodies: host.collision.ids().length, sky: skyLab.state(), ...recording() })
          : () => ({ scene: name, step: o.runtime.clock.step, bodies: host.collision.ids().length, pusher: host.collision.pose("pusher").position, ...recording() }),
  };
}

/** Where the perf tour's scripted fight stands: the open line north of the pond, flat and clear of the course. */
const TOUR_SPOT = { x: 0, z: 20, yaw: 0 };
/** The scripted fight's cast: 12 around the hero, 4 of them (one of each kind but the Warden, and a second wisp)
 *  defeated at once, so 8 stay engaged while 4 ragdolls fall (gate B7). */
const TOUR_CAST = ["wisp", "wisp", "wisp", "wisp", "wisp", "shade", "shade", "shade", "brute", "brute", "brute", "warden"] as const;
const TOUR_DEFEATS = [0, 1, 5, 8];

/** The handle keys of the valley's fight (`__rpg.fight`): the fight's own (spawn, reset, damage, events, tally…), its
 *  summary, the hero's intents, the lock, the view's report, and gate B7's perf tour over a scripted fight. */
function valleyFightHandle(d: {
  fight: () => Fight;
  runtime: Runtime;
  view: () => ValleyFightView | null;
  targeting: () => Targeting | null;
  host: ReturnType<typeof createSimHost>;
  rig: () => ValleyRig;
  renderer: THREE.WebGLRenderer | null;
  scene: THREE.Scene | null;
  look: () => LowPolyLook | null;
  tier: string;
}) {
  const { fight, runtime, view, targeting } = d;
  /** The scripted fight's numbers, sampled every tick of its segment (gate B7). */
  const scripted = { frames: 0, minEngaged: Infinity, minRagdolls: Infinity, maxEngaged: 0, maxRagdolls: 0, simStep: null as null | { samples: number; meanMs: number; p95Ms: number; maxMs: number } };
  const stage = (): void => {
    const f = fight();
    f.reset({ director: false });
    d.rig().warpHero(TOUR_SPOT.x, d.rig().terrainAt(TOUR_SPOT.x, TOUR_SPOT.z), TOUR_SPOT.z, TOUR_SPOT.yaw);
    const ids = TOUR_CAST.map((kind, i) => {
      const a = (i / TOUR_CAST.length) * Math.PI * 2;
      const r = kind === "shade" ? 7 : kind === "warden" ? 5 : 3.5;
      return f.spawn(kind, TOUR_SPOT.x + Math.sin(a) * r, TOUR_SPOT.z + Math.cos(a) * r, { alert: true });
    });
    for (const i of TOUR_DEFEATS) {
      const e = f.state().enemies.find((x) => x.id === ids[i]);
      const dx = e ? e.pos[0] - TOUR_SPOT.x : 0;
      const dz = e ? e.pos[2] - TOUR_SPOT.z : 1;
      const l = Math.hypot(dx, dz) || 1;
      f.defeat(ids[i]!, [dx / l, 0, dz / l], 0.9);
    }
  };
  return {
    ...fightHandle(fight, runtime),
    state: () => fightSummary(fight()),
    /** Ask for a hero intent at the next step (specs and shots drive the hero's moves). */
    heroIntent: (kind: "attack" | "dodge" | "skill" | "big" | "block" | "blockRelease", dir?: [number, number, number]) => void (fight().heroIntent = dir ? { kind, dir } : { kind }),
    /** Defeat an enemy at the next step, as a blow along `dir` would (it ragdolls). */
    defeat: (id: number, dir?: [number, number, number]) => fight().defeat(id, dir),
    /** A fighter's combat view (hp, poise, stamina, its move and defence; the hero is 0). */
    fighter: (id: number = HERO_ID) => fight().view(id),
    /** The lock (null: none) and the soft pick now. */
    lock: () => ({ ...(targeting()?.state() ?? { mode: "soft", locked: null, profile: null }), soft: targeting()?.soft() ?? null }),
    /** What is drawn: standing figures, ragdolls, motes; the impact director's report. */
    view: () => {
      const v = view();
      return v ? { figures: v.figures.report(), held: v.held(), feedback: v.feedback.report() } : null;
    },
    /** Stage gate B7's scripted fight now (12 enemies round the hero on the open line, 4 defeated at once). */
    stage,
    /** Gate B7: the perf tour over the scripted fight (fast-forward `ffMs` game ms per drawn frame); read `perf()`. */
    tour: (ffMs = 100) => {
      const look = d.look();
      if (!d.renderer || !d.scene || !look) throw new Error("fight tour: no renderer");
      const ui = overlay(d.tier === "low" ? "low" : "high");
      document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
      const t0 = { s: 0 };
      Object.assign(scripted, { frames: 0, minEngaged: Infinity, minRagdolls: Infinity, maxEngaged: 0, maxRagdolls: 0, simStep: null });
      const count = (): { engaged: number; ragdolls: number } => {
        const f = fight();
        return { engaged: f.state().enemies.filter((e) => e.alive && e.percept.level === "alert").length, ragdolls: f.bodies.state().ragdolls.length };
      };
      const segs: TourSegment[] = [
        { name: "warmup", start: () => d.rig().warpHero(TOUR_SPOT.x, d.rig().terrainAt(TOUR_SPOT.x, TOUR_SPOT.z), TOUR_SPOT.z, TOUR_SPOT.yaw), tick: (t) => t - t0.s > 1, max: 3 },
        {
          name: "fight",
          start: () => {
            stage();
            d.host.stepStats(true);
          },
          // 8 engaged and 4 ragdolls at once: sampled from 0.2 s in (the defeats land on the first step) to 1.4 s (a
          // wisp's one-part ragdoll settles in under 2 s, then dissolves)
          tick: (t) => {
            const at = t - t0.s;
            if (at >= 0.2) {
              const c = count();
              scripted.frames++;
              scripted.minEngaged = Math.min(scripted.minEngaged, c.engaged);
              scripted.maxEngaged = Math.max(scripted.maxEngaged, c.engaged);
              scripted.minRagdolls = Math.min(scripted.minRagdolls, c.ragdolls);
              scripted.maxRagdolls = Math.max(scripted.maxRagdolls, c.ragdolls);
              // the hero swings at whoever is nearest, now and then
              if (scripted.frames % 6 === 0) fight().heroIntent = { kind: "attack" };
            }
            if (at > 1.4) {
              scripted.simStep = d.host.stepStats(false);
              return true;
            }
            return false;
          },
          max: 6,
        },
      ];
      for (const sg of segs) {
        const st = sg.start;
        sg.start = () => {
          t0.s = runtime.clock.time;
          st?.();
        };
      }
      runTour({
        kind: "valley-fight",
        renderer: d.renderer,
        scene: d.scene,
        composer: look.composer,
        game: { simT: () => runtime.clock.time, pause: (on) => void (runtime.paused = on), advanceTime: (ms, draw) => void runtime.advance(ms, { draw: draw ?? true }) },
        segments: segs,
        ffMs,
        route: "valley-fight",
        estSeconds: 3,
        tier: d.tier === "low" ? "low" : "high",
        ui,
        readyAt: performance.now(),
        quality: () => ({ ...look.report() }),
        extra: () => ({ fight: { ...scripted }, enemies: fight().state().enemies.length }),
      });
    },
    /** The perf tour's report (null before a tour; status "running", then "done"). */
    perf: () => perfReport(),
  };
}
